import type { LucideIcon } from "lucide-react";
import type { SessionOrigin } from "@/api/auth";
import {
  Activity,
  Building2,
  GitBranch,
  Hash,
  Phone,
  Settings2,
} from "lucide-react";

/**
 * The option C navigation model: the rail switches domains, the second
 * column lists that domain's sections (or, for Routing and Accounts, the
 * objects themselves). Account-scoped domains live under /a/:accountId.
 */
export type DomainScope = "account" | "platform";

export interface Section {
  id: string;
  label: string;
  /** Group label in the column; sections without one sit at the top. */
  group?: string;
  /** Crossbar noun (or nouns) to probe on boot; the section hides when any of them 404. */
  probe?: string | string[];
  /** Hidden unless the operator meets it (see auth/operator.ts). */
  requires?: "superduper" | "reseller";
  /** Short note shown on the placeholder until the screen exists. */
  endpoints?: string[];
}

export interface Domain {
  id: string;
  label: string;
  icon: LucideIcon;
  scope: DomainScope;
  sections: Section[];
}

export const DOMAINS: Domain[] = [
  {
    id: "accounts",
    label: "Accounts",
    icon: Building2,
    scope: "platform",
    sections: [
      { id: "browse", label: "Accounts", endpoints: ["GET /v2/search?t=account", "GET .../descendants", "GET .../children"] },
      { id: "new", label: "New account", endpoints: ["PUT /v2/accounts/{parent}", "402 accept_charges", "PUT .../users (first admin)", "POST .../limits", "POST .../services"] },
    ],
  },
  {
    id: "pbx",
    label: "PBX",
    icon: Phone,
    scope: "account",
    sections: [
      { id: "overview", label: "Overview", endpoints: ["GET .../registrations/count", "GET .../devices/status", "GET .../channels", "GET .../alerts"] },
      { id: "users", label: "Users" },
      { id: "devices", label: "Devices" },
      { id: "groups", label: "Groups", endpoints: ["GET/PUT .../groups"] },
      { id: "call-center", label: "Call center", probe: ["queues", "agents"], endpoints: ["GET/PUT .../queues", ".../queues/{id}/agents", ".../queues/{id}/status", "GET .../agents"] },
      { id: "voicemail", label: "Voicemail", endpoints: ["GET/PUT .../vmboxes", "GET .../vmboxes/{id}/messages", "GET .../messages/{id}/raw"] },
      { id: "conferences", label: "Conferences", endpoints: ["GET/PUT .../conferences", "GET .../conferences/{id}/participants", "conference.event.*.{id}.* (socket)"] },
      { id: "fax", label: "Fax", endpoints: ["GET/PUT .../faxboxes", "GET .../faxes/inbox", "GET .../faxes/outbox", "PUT .../faxes (multipart)"] },
      { id: "media", label: "Media", group: "Library", endpoints: ["GET/PUT .../media", "POST .../media/{id}/raw", "GET .../media/prompts"] },
      { id: "directories", label: "Directories", group: "Library", endpoints: ["GET/PUT .../directories"] },
      { id: "feature-codes", label: "Feature codes", group: "Library", endpoints: ["callflows with featurecode", "GET .../metaflows", "GET/PATCH .../configs/callcenter"] },
      { id: "settings", label: "Account settings", group: "Account", endpoints: ["GET/PATCH .../accounts/{id}", "caller_id, timezone, language, music_on_hold", "GET/POST .../limits", "GET .../security"] },
      // The provisioner is not crossbar, so `probe` cannot gate this one:
      // the page itself says when /provision does not answer.
      { id: "provisioning", label: "Provisioning", group: "Account", endpoints: ["GET/PUT/PATCH /provision/api/v1/accounts/{id}/settings", ".../credentials/rotate", ".../profiles/{name}", ".../devices", ".../reconcile"] },
    ],
  },
  {
    id: "numbers",
    label: "Numbers",
    icon: Hash,
    scope: "account",
    sections: [
      { id: "in-service", label: "In service", endpoints: ["GET .../phone_numbers?filter_state=in_service", "PATCH .../phone_numbers/{num} (cnam, e911, failover, prepend, sms)"] },
      { id: "spare", label: "Spare", endpoints: ["GET .../phone_numbers (no used_by)", "PUT .../phone_numbers/{num}/activate", "DELETE .../phone_numbers/{num}"] },
      { id: "buy", label: "Buy numbers", endpoints: ["GET /v2/phone_numbers?prefix=", "PUT .../phone_numbers/collection/activate", "402 accept_charges"] },
      { id: "ports", label: "Port requests", endpoints: ["GET/PUT .../port_requests", "PATCH .../port_requests/{id}/{state}", ".../attachments", ".../timeline", ".../loa"] },
    ],
  },
  {
    id: "routing",
    label: "Routing",
    icon: GitBranch,
    scope: "account",
    sections: [
      { id: "callflows", label: "Callflows", endpoints: ["GET/PUT/POST .../callflows", "flow: {module, data, children}", "object.*.callflow (socket)"] },
      { id: "menus", label: "Menus", group: "Building blocks", endpoints: ["GET/PUT .../menus"] },
      { id: "time-of-day", label: "Time of day", group: "Building blocks", endpoints: ["GET/PUT .../temporal_rules", ".../temporal_rules_sets"] },
      { id: "blacklists", label: "Blacklists", group: "Building blocks", endpoints: ["GET/PUT .../blacklists"] },
      { id: "dialplans", label: "Dialplans", group: "Building blocks", probe: "dialplans", endpoints: ["GET .../dialplans"] },
    ],
  },
  {
    id: "insight",
    label: "Insight",
    icon: Activity,
    scope: "account",
    sections: [
      { id: "live", label: "Live calls", endpoints: ["GET .../channels", "call.*.* (socket)", "GET .../parked_calls"] },
      { id: "queues", label: "Queues", probe: ["queues", "agents"], endpoints: ["GET .../queues/{id}/status", "callcenter.queue|agent|call.* (socket)", "PUT .../queues/{id}/pickup", ".../eavesdrop"] },
      { id: "history", label: "Call history", endpoints: ["GET .../cdrs/interaction", "GET .../cdrs/legs/{id}", "Accept: text/csv"] },
      { id: "registrations", label: "Registrations", endpoints: ["GET .../registrations", "DELETE .../registrations/{username}"] },
      { id: "recordings", label: "Recordings", probe: "recordings", endpoints: ["GET .../recordings"] },
      { id: "alerts", label: "Alerts", endpoints: ["GET .../alerts"] },
      { id: "webhooks", label: "Webhooks", group: "Integrations", endpoints: ["GET/PUT .../webhooks", ".../webhooks/{id}/attempts", "GET /v2/webhooks/samples"] },
      { id: "api-access", label: "API access", group: "Integrations", endpoints: ["GET/PUT .../api_key", "GET .../websockets"] },
    ],
  },
  {
    id: "platform",
    label: "Platform",
    icon: Settings2,
    scope: "platform",
    sections: [
      { id: "carriers", label: "Carriers", endpoints: ["GET/PUT /v2/resources", "/v2/resources/jobs"] },
      { id: "rates", label: "Rates", endpoints: ["GET/PUT /v2/rates", "/v2/rates/ratedecks", "/v2/rates/number/{num}"] },
      { id: "ports", label: "All port requests", endpoints: ["GET /v2/port_requests?by_types=", "/v2/port_requests/last_submitted"] },
      { id: "numbers", label: "Number inventory", endpoints: ["GET /v2/phone_numbers", "GET /v2/phone_numbers/carriers_info", "/fix"] },
      { id: "notifications", label: "Email templates", group: "System", endpoints: ["GET/POST /v2/notifications/{id}", ".../preview", ".../smtplog"] },
      { id: "system-configs", label: "System configs", group: "System", requires: "superduper", endpoints: ["GET/PATCH /v2/system_configs/{id}[/{node}]", "GET /v2/schemas/system_config.{id}"] },
      { id: "stir", label: "STIR/SHAKEN", group: "System", requires: "superduper", probe: "stir", endpoints: ["GET /v2/stir", "GET/POST /v2/stir/keys", ".../keys/{kid}/certificate", "/v2/stir/trust", "/v2/stir/credentials", "GET/POST /v2/system_configs/stir"] },
      { id: "provisioning", label: "Provisioning", group: "System", requires: "superduper", endpoints: ["GET/PUT /provision/api/v1/platform/settings", ".../platform/ztp/credentials", ".../platform/mac/{mac}", ".../platform/log", "GET .../models"] },
      { id: "tasks", label: "Tasks and jobs", group: "System", endpoints: ["GET/PUT /v2/tasks", ".../input (csv)", ".../output"] },
      { id: "status", label: "Cluster status", group: "System", endpoints: ["GET /v2/system_status", "GET /v2/about"] },
      { id: "storage", label: "Storage plans", group: "System", endpoints: ["GET/PUT /v2/storage", "/v2/storage/plans"] },
      { id: "whitelabel", label: "Whitelabel", group: "System", requires: "reseller", endpoints: ["GET/POST /v2/whitelabel", ".../logo", ".../domains"] },
    ],
  },
];

/**
 * Domains a portal session never sees. The portal owns the company and
 * its users, and a portal token is scoped to one account by
 * `token_restrictions`, so the tree and the platform-wide screens have
 * nothing to show it and would only answer 403.
 */
const PORTAL_HIDDEN_DOMAINS = new Set(["accounts", "platform"]);

export function domainsFor(origin: SessionOrigin): Domain[] {
  return origin === "portal" ? DOMAINS.filter((d) => !PORTAL_HIDDEN_DOMAINS.has(d.id)) : DOMAINS;
}

export const domainById = (id: string | undefined, domains: Domain[] = DOMAINS) => domains.find((d) => d.id === id);

export function domainPath(domain: Domain, accountId?: string, sectionId?: string): string {
  const section = sectionId ?? domain.sections[0]?.id;
  if (domain.scope === "platform") return `/${domain.id}/${section}`;
  return `/a/${accountId ?? "_"}/${domain.id}/${section}`;
}
